import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, API } from "@/lib/api";
import { TOKEN_KEY } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/lib/permissions";
import { toast } from "sonner";
import {
  Database, Plus, Trash2, Upload, Send, Building2, Globe, Plug,
  FlaskConical, FileSpreadsheet, Wand2,
} from "lucide-react";

const STATUS = {
  active: { chip: "bg-emerald-100 text-emerald-700", label: "Active" },
  available_on_request: { chip: "bg-amber-100 text-amber-700", label: "On request" },
};

const SEV_MODES = [
  { value: "constant", label: "Fixed value" },
  { value: "direct", label: "Map from field" },
  { value: "score", label: "Score bands" },
];

function ConnectorCard({ c }) {
  const s = STATUS[c.status] || STATUS.available_on_request;
  return (
    <div className="rounded-md border border-black/5 bg-white p-4" data-testid={`connector-${c.key}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-ek-purple/10 text-ek-purple"><Plug size={16} /></span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${s.chip}`}>{s.label}</span>
      </div>
      <h4 className="mt-3 font-display text-sm font-semibold text-ek-ink">{c.name}</h4>
      <p className="mt-0.5 text-[11px] uppercase tracking-wide text-ek-ink/65">{c.category} · {c.auth}</p>
      <p className="mt-1.5 text-xs text-ek-ink/65">{c.description}</p>
    </div>
  );
}

export default function Ingestion() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canIngest = hasPermission(user, "signal.ingest");

  const [tenant, setTenant] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [sources, setSources] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  // create form
  const [name, setName] = useState("");
  const [type, setType] = useState("csv");
  const [extId, setExtId] = useState("ref");
  const [subjField, setSubjField] = useState("title");
  const [sumField, setSumField] = useState("description");
  const [tsField, setTsField] = useState("raised_on");
  const [sevMode, setSevMode] = useState("direct");
  const [sevField, setSevField] = useState("rag");

  // push tester
  const [pushTarget, setPushTarget] = useState(null);
  const [pushJson, setPushJson] = useState('[\n  {"ref":"R1","title":"Vendor outage","description":"Tier-1 disruption","rag":"Red"}\n]');

  const load = useCallback(() => {
    api.get("/tenant").then(({ data }) => setTenant(data)).catch(() => {});
    api.get("/ingest/connectors").then(({ data }) => setConnectors(data.connectors)).catch(() => {});
    api.get("/ingest/sources").then(({ data }) => setSources(data.items)).catch(() => toast.error("Could not load sources"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const buildMapping = () => {
    const sev =
      sevMode === "constant" ? { mode: "constant", value: "medium" }
        : sevMode === "score" ? { mode: "score", field: sevField, bands: [[76, "critical"], [51, "high"], [26, "medium"], [0, "low"]] }
          : { mode: "direct", field: sevField, map: { Red: "critical", Amber: "medium", Green: "low" } };
    return { external_id: extId, subject_field: subjField, subject_type: "entity", summary_field: sumField, received_at_field: tsField, severity: sev };
  };

  const analyzeFile = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await fetch(`${API}/ingest/analyze`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Analyze failed");
      const m = data.suggested_mapping || {};
      if (m.external_id) setExtId(m.external_id);
      if (m.subject_field) setSubjField(m.subject_field);
      if (m.summary_field) setSumField(m.summary_field);
      if (m.received_at_field) setTsField(m.received_at_field);
      if (m.severity?.mode) { setSevMode(m.severity.mode); if (m.severity.field) setSevField(m.severity.field); }
      toast.success(`Detected ${data.columns.length} columns — mapping suggested`);
    } catch (e) { toast.error(e.message || "Analyze failed"); }
  };

  const createSource = async () => {
    if (!name.trim()) return toast.error("Name the source");
    try {
      await api.post("/ingest/sources", { name, type, mapping: buildMapping() });
      toast.success("Source created");
      setShowCreate(false); setName("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteSource = async (id) => {
    try { await api.delete(`/ingest/sources/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const uploadFile = async (id, file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await fetch(`${API}/ingest/sources/${id}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      toast.success(`Ingested ${data.ingested} · skipped ${data.skipped_duplicates}`);
      load();
    } catch (e) { toast.error(e.message || "Upload failed"); }
  };

  const pushRows = async () => {
    let rows;
    try { rows = JSON.parse(pushJson); } catch { return toast.error("Invalid JSON"); }
    try {
      const { data } = await api.post(`/ingest/sources/${pushTarget}/push`, { rows });
      toast.success(`Ingested ${data.ingested} · skipped ${data.skipped_duplicates}`);
      setPushTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Push failed"); }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Ingestion adapters" description="Standardised, tenant-aware data ingestion." path="/ingestion" />
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-10" data-testid="ingestion-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="ek-eyebrow">{t("ingestion.eyebrow")}</h1>
            <h2 className="mt-1 font-display text-3xl font-bold text-ek-ink">{t("ingestion.title")}</h2>
            <p className="mt-2 max-w-2xl text-sm text-ek-ink/65">{t("ingestion.subtitle")}</p>
          </div>
          {tenant && (
            <div className="rounded-md border border-black/5 bg-white px-4 py-3 text-xs" data-testid="tenant-banner">
              <p className="flex items-center gap-1.5 font-semibold text-ek-ink"><Building2 size={13} className="text-ek-purple" /> {tenant.name}</p>
              <p className="mt-1 flex items-center gap-1.5 text-ek-ink/65"><Globe size={12} /> {tenant.region?.toUpperCase()} · {tenant.locale} · {tenant.currency}</p>
            </div>
          )}
        </div>

        {/* Connectors */}
        <h3 className="mt-10 ek-eyebrow">{t("ingestion.connectors")}</h3>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="connector-grid">
          {connectors.map((c) => <ConnectorCard key={c.key} c={c} />)}
        </div>

        {/* Sources */}
        <div className="mt-10 flex items-center justify-between">
          <h3 className="ek-eyebrow flex items-center gap-1"><Database size={12} /> {t("ingestion.sources")}</h3>
          {canIngest && (
            <button onClick={() => setShowCreate((v) => !v)} className="ek-btn flex items-center gap-1.5" data-testid="source-new-button">
              <Plus size={15} /> {t("ingestion.newSource")}
            </button>
          )}
        </div>

        {showCreate && canIngest && (
          <div className="mt-4 rounded-md border border-ek-purple/20 bg-white p-5" data-testid="source-create-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-semibold text-ek-ink/60">{t("ingestion.name")}
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="source-name-input" className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 text-sm font-normal text-ek-ink" placeholder="Internal risk register" />
              </label>
              <label className="text-xs font-semibold text-ek-ink/60">{t("ingestion.type")}
                <select value={type} onChange={(e) => setType(e.target.value)} data-testid="source-type-select" className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 text-sm font-normal">
                  <option value="csv">Spreadsheet (CSV/XLSX)</option>
                  <option value="rest_push">REST push (JSON)</option>
                </select>
              </label>
            </div>
            <p className="mt-4 ek-eyebrow">Field mapping</p>
            <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-ek-purple/30 bg-ek-purple/5 px-3 py-1.5 text-xs font-semibold text-ek-purple" data-testid="mapping-wizard-upload">
              <Wand2 size={14} /> Auto-detect from a sample file
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => analyzeFile(e.target.files[0])} />
            </label>
            <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[["ID column", extId, setExtId], ["Subject column", subjField, setSubjField], ["Summary column", sumField, setSumField], ["Date column", tsField, setTsField]].map(([lbl, val, set]) => (
                <label key={lbl} className="text-xs text-ek-ink/65">{lbl}
                  <input value={val} onChange={(e) => set(e.target.value)} className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5 text-sm" data-testid={`map-${lbl.split(" ")[0].toLowerCase()}`} />
                </label>
              ))}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="text-xs text-ek-ink/65">Severity strategy
                <select value={sevMode} onChange={(e) => setSevMode(e.target.value)} data-testid="map-severity-mode" className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5 text-sm">
                  {SEV_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
              {sevMode !== "constant" && (
                <label className="text-xs text-ek-ink/65">Severity column
                  <input value={sevField} onChange={(e) => setSevField(e.target.value)} className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5 text-sm" data-testid="map-severity-field" />
                </label>
              )}
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={createSource} className="ek-btn" data-testid="source-create-submit">{t("ingestion.create")}</button>
              <button onClick={() => setShowCreate(false)} className="ek-btn-outline">Cancel</button>
            </div>
          </div>
        )}

        <div className="mt-4 space-y-2" data-testid="source-list">
          {sources.length === 0 ? (
            <p className="rounded-md border border-black/5 bg-white p-5 text-sm text-ek-ink/65">{t("ingestion.noSources")}</p>
          ) : sources.map((s) => (
            <div key={s.source_id} className="rounded-md border border-black/5 bg-white p-4" data-testid={`source-${s.source_id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-md bg-ek-canvas text-ek-purple">
                  {s.type === "csv" ? <FileSpreadsheet size={16} /> : <Send size={16} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ek-ink">{s.name}</p>
                  <p className="text-xs text-ek-ink/65">{s.type} · {s.signal_count} {t("ingestion.signals")} · {s.mapping?.subject_field} → subject</p>
                </div>
                {canIngest && (
                  <div className="flex items-center gap-2">
                    {s.type === "csv" ? (
                      <label className="ek-btn-outline !py-1.5 cursor-pointer text-xs" data-testid={`source-upload-${s.source_id}`}>
                        <Upload size={13} /> {t("ingestion.upload")}
                        <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => uploadFile(s.source_id, e.target.files[0])} />
                      </label>
                    ) : (
                      <button onClick={() => setPushTarget(pushTarget === s.source_id ? null : s.source_id)} className="ek-btn-outline !py-1.5 text-xs" data-testid={`source-push-${s.source_id}`}>
                        <FlaskConical size={13} /> {t("ingestion.push")}
                      </button>
                    )}
                    <button onClick={() => deleteSource(s.source_id)} className="ek-btn-outline !py-1.5 text-xs" data-testid={`source-delete-${s.source_id}`}><Trash2 size={13} /></button>
                  </div>
                )}
              </div>
              {pushTarget === s.source_id && (
                <div className="mt-3 border-t border-black/5 pt-3" data-testid={`push-panel-${s.source_id}`}>
                  <textarea value={pushJson} onChange={(e) => setPushJson(e.target.value)} rows={5} className="w-full rounded-md border border-black/10 p-2 font-mono text-xs" data-testid="push-json-input" />
                  <button onClick={pushRows} className="ek-btn mt-2 !py-1.5 text-xs" data-testid="push-submit"><Send size={13} /> Ingest rows</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
